import { DEFAULT_MAX_GUESTS } from '../config'
import {
  LAYOUT_COLUMNS,
  Page,
  Row,
  RowLayout,
  WebsiteDocument
} from '../schema/document'
import {
  ElementType,
  FULL_WIDTH_ELEMENT_TYPES,
  PageElement,
  isFullWidthElement
} from '../schema/elements'
import { emptyRichText } from '../schema/richText'
import { nextId } from './ids'

/**
 * The line a freshly dropped booking form asks a guest to agree to. Generic
 * on purpose: the builder does not know the venue's legal name, and a wrong
 * name is worse than "the venue". The template writes the named version.
 */
const BOOKING_CONSENT_DEFAULT: Record<string, string> = {
  pl: 'Wyrażam zgodę na przetwarzanie moich danych przez lokal w celu obsługi rezerwacji.',
  en: 'I agree to the venue processing my details in order to handle my booking.'
}

/**
 * Every change the editor can make to a document, as pure functions.
 *
 * No React, no store, no I/O: an operation takes a document and returns a new
 * one. That is what makes undo a stack of documents rather than a stack of
 * hand-written inverse operations, and it is what lets the rules below be
 * tested without mounting anything.
 */

const emptyColumn = () => ({
  id: nextId('col'),
  align: 'left' as const,
  items: []
})

export function createRow(layout: RowLayout): Row {
  return {
    id: nextId('row'),
    layout,
    background: { type: 'none' },
    paddingY: 'M',
    maxWidth: 'contained',
    verticalAlign: 'top',
    gap: 'M',
    mobileBehavior: 'stack',
    hidden: false,
    columns: LAYOUT_COLUMNS[layout].map(emptyColumn)
  }
}

/**
 * A new element of the given type, filled in for the language being edited.
 *
 * Defaults are a working element rather than an empty one, so dropping a button
 * onto a page produces a button that says something instead of a blank shape.
 */
export function createElement(
  type: ElementType,
  language: string,
  /** The connected Google place's embed URL, for a map; '' when there is none. */
  mapEmbedUrl = ''
): PageElement {
  const id = nextId(type)
  switch (type) {
    case 'heading':
      return { id, type, level: 2, text: { [language]: '' }, align: 'left' }
    case 'text':
      return { id, type, content: { [language]: emptyRichText }, align: 'left' }
    case 'image':
      return {
        id,
        type,
        assetId: '',
        alt: { [language]: '' },
        ratio: '16:9',
        fit: 'cover',
        focalPoint: { x: 0.5, y: 0.5 },
        rounded: true
      }
    case 'button':
      return {
        id,
        type,
        label: { [language]: '' },
        target: { kind: 'url', href: 'https://' },
        variant: 'primary',
        align: 'left'
      }
    case 'gallery':
      return { id, type, assetIds: [], columns: 3, lightbox: true }
    case 'video':
      return { id, type, provider: 'youtube', url: '' }
    case 'map':
      // Empty, it waits for the venue's Google place; see `mapElementSchema`.
      return { id, type, embedUrl: mapEmbedUrl, height: 'M' }
    case 'openingHours':
      return {
        id,
        type,
        source: 'manual',
        entries: [0, 1, 2, 3, 4, 5, 6].map(weekday => ({
          weekday,
          closed: false,
          opensAt: '12:00',
          closesAt: '22:00'
        }))
      }
    case 'menuFromPos':
      return {
        id,
        type,
        categoryIds: [],
        layout: 'list',
        showPrices: true,
        showPhotos: false
      }
    case 'contactForm':
      return { id, type, formId: '' }
    case 'reservation':
      /*
        No room until the panel is given one, and without a room the block
        draws nothing: the plan is the picker, and a booking form with no
        table to pick is phase 4's mode A. The consent line is seeded because
        a form without one may not go out (brief §7); it cannot name the venue
        from here, so it says "the venue" until the owner writes it properly.
      */
      return {
        id,
        type,
        variant: 'inline',
        maxGuests: DEFAULT_MAX_GUESTS,
        consent: {
          [language]:
            BOOKING_CONSENT_DEFAULT[language] ?? BOOKING_CONSENT_DEFAULT.en
        }
      }
    case 'spacer':
      return { id, type, size: 'M' }
    default:
      return { id, type: 'divider' }
  }
}

/* ------------------------------------------------------------------ rules */

/**
 * Whether an element of this type may go into this column.
 *
 * The composition rule from §4 of the brief, asked in advance so the palette can
 * grey the option out and say why, instead of letting someone place it and then
 * refusing to save.
 */
export function canPlaceElement(
  row: Row,
  columnId: string,
  type: ElementType
):
  | { allowed: true }
  | { allowed: false; reason: 'layout' | 'occupied' | 'wideOccupant' } {
  const column = row.columns.find(item => item.id === columnId)

  if (!(FULL_WIDTH_ELEMENT_TYPES as readonly string[]).includes(type)) {
    /*
      The rule in `rowSchema` is symmetric: a full-width block has to be the
      ONLY element in its column. So an ordinary element is refused next to one
      just as a full-width one is refused next to anything, whichever arrived
      first. Asking only the first half of that let a heading be dropped beside
      a reservation widget, and the document then failed to publish with a zod
      path for a message.
    */
    if (column?.items.some(isFullWidthElement)) {
      return { allowed: false, reason: 'wideOccupant' }
    }
    return { allowed: true }
  }

  if (row.layout !== '1') return { allowed: false, reason: 'layout' }
  if (column && column.items.length > 0)
    return { allowed: false, reason: 'occupied' }
  return { allowed: true }
}

/** A row holding a full-width element cannot be split into columns. */
export function canChangeLayout(row: Row, layout: RowLayout): boolean {
  if (layout === '1') return true
  return !row.columns.some(column => column.items.some(isFullWidthElement))
}

/* ------------------------------------------------------------- page edits */

function mapPage(
  doc: WebsiteDocument,
  pageId: string,
  change: (page: Page) => Page
): WebsiteDocument {
  return {
    ...doc,
    pages: doc.pages.map(page => (page.id === pageId ? change(page) : page))
  }
}

function mapRow(
  doc: WebsiteDocument,
  pageId: string,
  rowId: string,
  change: (row: Row) => Row
): WebsiteDocument {
  return mapPage(doc, pageId, page => ({
    ...page,
    blocks: page.blocks.map(row => (row.id === rowId ? change(row) : row))
  }))
}

export function addRow(
  doc: WebsiteDocument,
  pageId: string,
  layout: RowLayout,
  atIndex?: number
): WebsiteDocument {
  const row = createRow(layout)
  return mapPage(doc, pageId, page => {
    const blocks = [...page.blocks]
    blocks.splice(atIndex ?? blocks.length, 0, row)
    return { ...page, blocks }
  })
}

export function moveRow(
  doc: WebsiteDocument,
  pageId: string,
  rowId: string,
  direction: -1 | 1
): WebsiteDocument {
  return mapPage(doc, pageId, page => {
    const index = page.blocks.findIndex(row => row.id === rowId)
    const target = index + direction
    if (index < 0 || target < 0 || target >= page.blocks.length) return page
    const blocks = [...page.blocks]
    const [row] = blocks.splice(index, 1)
    blocks.splice(target, 0, row)
    return { ...page, blocks }
  })
}

/** Deep copy with fresh ids, so the copy is a separate row and not an alias. */
export function duplicateRow(
  doc: WebsiteDocument,
  pageId: string,
  rowId: string
): WebsiteDocument {
  return mapPage(doc, pageId, page => {
    const index = page.blocks.findIndex(row => row.id === rowId)
    if (index < 0) return page
    const source = page.blocks[index]
    const copy: Row = {
      ...source,
      id: nextId('row'),
      columns: source.columns.map(column => ({
        ...column,
        id: nextId('col'),
        items: column.items.map(item => ({ ...item, id: nextId(item.type) }))
      }))
    }
    const blocks = [...page.blocks]
    blocks.splice(index + 1, 0, copy)
    return { ...page, blocks }
  })
}

export function deleteRow(
  doc: WebsiteDocument,
  pageId: string,
  rowId: string
): WebsiteDocument {
  return mapPage(doc, pageId, page => ({
    ...page,
    blocks: page.blocks.filter(row => row.id !== rowId)
  }))
}

export function updateRow(
  doc: WebsiteDocument,
  pageId: string,
  rowId: string,
  patch: Partial<Omit<Row, 'id' | 'columns' | 'layout'>>
): WebsiteDocument {
  return mapRow(doc, pageId, rowId, row => ({ ...row, ...patch }))
}

/**
 * Changes a row's layout, adding or removing columns to match.
 *
 * Content is never dropped: items from a column that disappears move into the
 * last one that stays. A layout change is a formatting decision, and silently
 * deleting somebody's paragraph because they tried three columns instead of two
 * is the kind of thing that stops people trusting an editor.
 */
export function changeRowLayout(
  doc: WebsiteDocument,
  pageId: string,
  rowId: string,
  layout: RowLayout
): WebsiteDocument {
  return mapRow(doc, pageId, rowId, row => {
    if (!canChangeLayout(row, layout)) return row

    const wanted = LAYOUT_COLUMNS[layout].length
    if (row.columns.length === wanted) return { ...row, layout }

    if (wanted > row.columns.length) {
      const added = Array.from(
        { length: wanted - row.columns.length },
        emptyColumn
      )
      return { ...row, layout, columns: [...row.columns, ...added] }
    }

    const kept = row.columns.slice(0, wanted)
    const orphaned = row.columns.slice(wanted).flatMap(column => column.items)
    const last = kept.length - 1
    return {
      ...row,
      layout,
      columns: kept.map((column, index) =>
        index === last
          ? { ...column, items: [...column.items, ...orphaned] }
          : column
      )
    }
  })
}

export function setColumnAlign(
  doc: WebsiteDocument,
  pageId: string,
  columnId: string,
  align: 'left' | 'center' | 'right'
): WebsiteDocument {
  return mapPage(doc, pageId, page => ({
    ...page,
    blocks: page.blocks.map(row => ({
      ...row,
      columns: row.columns.map(column =>
        column.id === columnId ? { ...column, align } : column
      )
    }))
  }))
}

/* ---------------------------------------------------------- element edits */

export function addElement(
  doc: WebsiteDocument,
  pageId: string,
  columnId: string,
  element: PageElement
): WebsiteDocument {
  return mapPage(doc, pageId, page => ({
    ...page,
    blocks: page.blocks.map(row => ({
      ...row,
      columns: row.columns.map(column =>
        column.id === columnId
          ? { ...column, items: [...column.items, element] }
          : column
      )
    }))
  }))
}

export function updateElement(
  doc: WebsiteDocument,
  pageId: string,
  elementId: string,
  patch: Record<string, unknown>
): WebsiteDocument {
  return mapPage(doc, pageId, page => ({
    ...page,
    blocks: page.blocks.map(row => ({
      ...row,
      columns: row.columns.map(column => ({
        ...column,
        items: column.items.map(item =>
          item.id === elementId ? ({ ...item, ...patch } as PageElement) : item
        )
      }))
    }))
  }))
}

/**
 * Every map on every page pointed at `embedUrl`, the venue's place as it is
 * connected now. A map shows the venue and has nothing of its own to set, so
 * it follows the connection the way the template's map section does when the
 * template rebuilds the page.
 *
 * The same document back when every map already shows that place, so a caller
 * can tell that nothing changed.
 */
export function followRestaurantMap(
  doc: WebsiteDocument,
  embedUrl: string
): WebsiteDocument {
  const isStale = doc.pages.some(page =>
    page.blocks.some(row =>
      row.columns.some(column =>
        column.items.some(
          item => item.type === 'map' && item.embedUrl !== embedUrl
        )
      )
    )
  )
  if (!isStale) return doc
  return {
    ...doc,
    pages: doc.pages.map(page => ({
      ...page,
      blocks: page.blocks.map(row => ({
        ...row,
        columns: row.columns.map(column => ({
          ...column,
          items: column.items.map(item =>
            item.type === 'map' ? { ...item, embedUrl } : item
          )
        }))
      }))
    }))
  }
}

export function deleteElement(
  doc: WebsiteDocument,
  pageId: string,
  elementId: string
): WebsiteDocument {
  return mapPage(doc, pageId, page => ({
    ...page,
    blocks: page.blocks.map(row => ({
      ...row,
      columns: row.columns.map(column => ({
        ...column,
        items: column.items.filter(item => item.id !== elementId)
      }))
    }))
  }))
}

export function moveElement(
  doc: WebsiteDocument,
  pageId: string,
  elementId: string,
  direction: -1 | 1
): WebsiteDocument {
  return mapPage(doc, pageId, page => ({
    ...page,
    blocks: page.blocks.map(row => ({
      ...row,
      columns: row.columns.map(column => {
        const index = column.items.findIndex(item => item.id === elementId)
        const target = index + direction
        if (index < 0 || target < 0 || target >= column.items.length)
          return column
        const items = [...column.items]
        const [item] = items.splice(index, 1)
        items.splice(target, 0, item)
        return { ...column, items }
      })
    }))
  }))
}

/** Finds a row, its column and an element by element id. */
export function locateElement(page: Page, elementId: string) {
  for (let r = 0; r < page.blocks.length; r += 1) {
    const row = page.blocks[r]
    for (let c = 0; c < row.columns.length; c += 1) {
      const column = row.columns[c]
      const element = column.items.find(item => item.id === elementId)
      if (element) return { row, column, element }
    }
  }
  return undefined
}
